import React from "react";
import { Lock, LockOpen, NewReleases, Star as StarIcon, VisibilityRounded } from "@mui/icons-material";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import { Box, Button, Card, CardContent, Chip, Tooltip, Typography } from "@mui/material";
import { NOTICE, TEXT } from "../../dark-theme/palette";
import UtilService from "../../services/UtilService";
import { usePagesTranslation } from "../locales";
import { TagChipAvatar } from "../puzzles/common/components/TagChipAvatar";
import JigsawDieLine3DOverlay from "../puzzles/jigsaw/JigsawDieLine3DOverlay";
import {
    JIGSAW_BROWSE_DIE_LINE_PATHS,
    JIGSAW_BROWSE_DIE_LINE_VIEWBOX,
} from "./jigsawBrowseDieLinePaths";

/** Compact tier badges — aligned with PuzzleCardBase. */
const tierBadgeChipSx = {
    pointerEvents: "auto",
    flexShrink: 0,
    maxWidth: "none",
    height: 22,
    "& .MuiChip-label": {
        px: 0.65,
        py: 0,
        fontSize: "0.68rem",
        lineHeight: 1.2,
        fontWeight: 600,
        whiteSpace: "nowrap",
    },
    "& .MuiChip-icon": {
        fontSize: "0.8rem",
        marginLeft: "4px",
        marginRight: "-2px",
    },
};

function getStoryTitle(story) {
    if (!story) return "";
    return story.english?.title ?? story.en?.title ?? story.french?.title ?? story.fr?.title ?? "";
}

function getImageUrl(story) {
    if (!story) return null;
    return story.imageIcon200Url ?? story.imageIcon400Url ?? story.imageUrl ?? story.imagePreviewUrl ?? null;
}

export const browseViewButtonSx = {
    backgroundColor: "rgba(56, 132, 255, 0.22)",
    color: TEXT.primary,
    border: `1px solid ${NOTICE.info.border}`,
    textTransform: "none",
    fontWeight: 700,
    letterSpacing: "0.02em",
    minHeight: 36,
    borderRadius: 999,
    boxShadow: "0 4px 14px rgba(37, 99, 235, 0.22)",
    transition: "transform 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease",
    "&:hover": {
        backgroundColor: "rgba(56, 132, 255, 0.36)",
        boxShadow: "0 6px 18px rgba(37, 99, 235, 0.34)",
        transform: "translateY(-1px)",
    },
    "&:active": {
        transform: "translateY(0)",
        boxShadow: "0 2px 8px rgba(37, 99, 235, 0.24)",
    },
};

/**
 * Jigsaw browse card — matches sliding card style.
 * @param {boolean} [showThumbsUp] — force thumbs-up badge (e.g. Top 5 feed); no play count shown.
 */
export default function JigsawBrowseStoryCard({
    story,
    storyJigsaw,
    tags,
    showThumbsUp = false,
    userPayPlan,
    onClick,
    onUpgrade,
}) {
    const { t } = usePagesTranslation();
    const storyId = story?.id;
    const title = getStoryTitle(story) || storyId || "—";
    const imageUrl = getImageUrl(story);
    const isAnonymous = !userPayPlan;
    const isLocked = false;
    const showPopularBadge = showThumbsUp === true;

    let publishedAgoStr = null;
    if (storyJigsaw?.publishedAt) {
        const pubMs = new Date(storyJigsaw.publishedAt).getTime();
        if (Number.isFinite(pubMs) && pubMs > 0) {
            publishedAgoStr = UtilService.formatRelativeTime(pubMs) || null;
        }
    }

    // NEW badge: published in last 1 day
    const isNew = (() => {
        if (!storyJigsaw?.publishedAt) return false;
        const pubMs = new Date(storyJigsaw.publishedAt).getTime();
        if (!Number.isFinite(pubMs)) return false;
        const now = Date.now();
        if (pubMs > now) return false;
        return pubMs >= now - 24 * 60 * 60 * 1000;
    })();

    // Tags from puzzleTemplate.tags prop, falling back to story genre
    const rawTags = Array.isArray(tags) && tags.length > 0
        ? tags
        : (story?.topic?.genre?.name ? [story.topic.genre.name] : []);
    const displayTags = rawTags
        .filter((tag) => tag.toLowerCase() !== "bedrock")
        .map((tag) => String(tag).trim().toLowerCase());

    const isFreePuzzle = storyJigsaw?.free === true;
    const isPaidPuzzle = storyJigsaw != null && storyJigsaw.free !== true;
    const hasTopBadges = isNew || showPopularBadge || isPaidPuzzle || isFreePuzzle;
    const popularLabel = t("puzzle.card.popular", "Popular");

    return (
        <Box
            sx={{
                position: "relative",
                width: "100%",
                maxWidth: "100%",
                minHeight: 0,
                overflow: "visible",
            }}
        >
            {hasTopBadges && (
                <Box
                    sx={{
                        position: "absolute",
                        top: -9,
                        left: 8,
                        right: 8,
                        zIndex: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        gap: 0.5,
                        pointerEvents: "none",
                    }}
                >
                    {isNew && (
                        <Chip
                            icon={<NewReleases />}
                            label={t("puzzle.card.new")}
                            size="small"
                            color="error"
                            variant="filled"
                            sx={{
                                ...tierBadgeChipSx,
                                backgroundColor: "#ff4444",
                                color: "white",
                                boxShadow: "0 1px 3px rgba(0,0,0,0.18)",
                                "& .MuiChip-label": {
                                    ...tierBadgeChipSx["& .MuiChip-label"],
                                    fontWeight: 700,
                                },
                            }}
                        />
                    )}
                    {isFreePuzzle && (
                        <Tooltip title={t("puzzle.card.free-puzzle")}>
                            <Chip
                                icon={<LockOpen />}
                                label={t("puzzle.card.free")}
                                size="small"
                                color="success"
                                variant="filled"
                                sx={tierBadgeChipSx}
                            />
                        </Tooltip>
                    )}
                    {isPaidPuzzle && (
                        <Tooltip title={t("puzzle.card.premium-puzzle")}>
                            <Chip
                                icon={<StarIcon />}
                                label={t("puzzle.card.premium")}
                                size="small"
                                color="warning"
                                variant="filled"
                                sx={tierBadgeChipSx}
                            />
                        </Tooltip>
                    )}
                    {showPopularBadge && (
                        <Tooltip title={popularLabel} arrow>
                            <Chip
                                icon={<ThumbUpIcon />}
                                size="small"
                                color="warning"
                                variant="filled"
                                aria-label={popularLabel}
                                sx={{
                                    ...tierBadgeChipSx,
                                    ml: "auto",
                                    backgroundColor: "#ff9800",
                                    color: "white",
                                    boxShadow: "0 1px 3px rgba(0,0,0,0.18)",
                                    minWidth: 22,
                                    width: 22,
                                    "& .MuiChip-label": {
                                        display: "none",
                                        px: 0,
                                        width: 0,
                                    },
                                    "& .MuiChip-icon": {
                                        margin: 0,
                                        fontSize: "0.85rem",
                                        color: "white",
                                    },
                                }}
                            />
                        </Tooltip>
                    )}
                </Box>
            )}

            <Card
                variant="outlined"
                sx={{
                    backgroundColor: "background.paper",
                    borderColor: "divider",
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "visible",
                    transition: "box-shadow 0.2s, border-color 0.2s",
                    "&:hover": {
                        boxShadow: 3,
                        borderColor: "primary.light",
                    },
                }}
            >
                <CardContent sx={{ pt: 1, flex: "1 1 0%", display: "flex", flexDirection: "column", pb: 1.5 }}>
                    {/* Title */}
                    <Typography
                        variant="subtitle1"
                        sx={{
                            fontWeight: 600,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            lineHeight: "1.75em",
                            minHeight: "3.5em",
                            mb: 0.5,
                        }}
                    >
                        {title}
                    </Typography>

                    {/* Image with die lines — no click */}
                    {imageUrl ? (
                        <Box
                            sx={{
                                position: "relative",
                                width: "100%",
                                aspectRatio: "1 / 1",
                                overflow: "hidden",
                                borderRadius: 1,
                                mb: 1.5,
                                bgcolor: "action.hover",
                            }}
                        >
                            <Box
                                component="img"
                                src={imageUrl}
                                alt=""
                                sx={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
                            />
                            <JigsawDieLine3DOverlay
                                paths={JIGSAW_BROWSE_DIE_LINE_PATHS}
                                viewBoxWidth={JIGSAW_BROWSE_DIE_LINE_VIEWBOX.width}
                                viewBoxHeight={JIGSAW_BROWSE_DIE_LINE_VIEWBOX.height}
                                preserveAspectRatio="xMidYMid meet"
                                strokeWidth={0.7}
                                zIndex={1}
                            />
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                width: "100%",
                                aspectRatio: "1 / 1",
                                bgcolor: "grey.200",
                                borderRadius: 1,
                                mb: 1.5,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <Typography color="text.secondary">No image</Typography>
                        </Box>
                    )}

                    {/* Tags + Published date */}
                    {(displayTags.length > 0 || publishedAgoStr) && (
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5, gap: 0.5 }}>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                {displayTags.map((tag) => (
                                    <Chip
                                        key={tag}
                                        avatar={<TagChipAvatar tag={tag} size={18} />}
                                        label={tag}
                                        size="small"
                                        variant="outlined"
                                        sx={{ fontSize: "0.7rem", height: 20 }}
                                    />
                                ))}
                            </Box>
                            {publishedAgoStr && (
                                <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                                    {publishedAgoStr}
                                </Typography>
                            )}
                        </Box>
                    )}

                    {/* Play / Upgrade button — same rules as SlidingCard / PuzzleCardBase */}
                    <Box sx={{ width: "100%", pt: 0.5, mt: "auto" }}>
                        {isLocked ? (
                            isAnonymous ? (
                                <Tooltip title={t("puzzle.card.play-sign-in-tooltip")} arrow>
                                    <span>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            color="primary"
                                            disabled
                                            startIcon={<Lock />}
                                        >
                                            {t("puzzle.card.play")}
                                        </Button>
                                    </span>
                                </Tooltip>
                            ) : (
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="secondary"
                                    startIcon={<Lock />}
                                    onClick={() => onUpgrade?.()}
                                >
                                    {t("puzzle.card.upgrade-to-play")}
                                </Button>
                            )
                        ) : (
                            <Button
                                size="small"
                                fullWidth
                                variant="contained"
                                startIcon={<VisibilityRounded />}
                                onClick={() => storyId && onClick?.(storyId)}
                                sx={browseViewButtonSx}
                            >
                                {t("puzzle.card.view")}
                            </Button>
                        )}
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
}
